import { z } from "zod";
/**
 * A record id as it crosses the wire in JSON — a uuid v4 since the move to PostgreSQL.
 *
 * The name is a leftover from the Mongo era (it validated a 24-char hex ObjectId) and is kept
 * because it is referenced from a dozen schemas; only the format changed. Leaving the old regex
 * in place would have silently rejected every real id — /device-api/status validates its
 * `messageId` through here, so the phone's status reports would all have 400'd.
 */
export const objectIdSchema = z
    .string()
    .regex(/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i, "Invalid identifier");
/** E.164: + followed by 8-15 digits. Normalization (libphonenumber-js) happens before this is applied. */
export const e164PhoneSchema = z
    .string()
    .regex(/^\+[1-9]\d{7,14}$/, "Phone number must be in E.164 format");
/**
 * The originating address of an inbound SMS, which is NOT always an E.164 phone number.
 *
 * Three shapes turn up in practice, all of them legitimate, and Android reports each verbatim in
 * SmsMessage.originatingAddress:
 *
 *   +923011116259  E.164, the only shape the old rule allowed
 *   03324012015    national format with no country code — an ordinary person texting from the
 *                  same country as the receiving SIM, which is the common case, not an edge case
 *   JazzCash       a GSM 03.38 alphanumeric sender ID, used by carriers, banks and 2FA services
 *   6055           a short code
 *
 * Why this matters far more than "we cannot display a sender name": the device uploads inbound
 * SMS in one batch, oldest first, and marks NOTHING as uploaded unless the whole batch is
 * accepted (see syncTask.ts). So a single unacceptable sender does not lose one message — it
 * becomes a permanent plug that blocks every message queued behind it, retried and rejected
 * every 15 seconds indefinitely. Widening this rule is what unjams that queue.
 *
 * Still bounded and charset-restricted rather than a free string: the value arrives from a
 * paired device and is rendered in the dashboard, so it must not be able to carry arbitrary
 * length, newlines or markup. 32 characters sits comfortably above the 11-character GSM
 * alphanumeric limit and well below anything that could disrupt a table cell.
 *
 * INBOUND ONLY. Outbound recipient validation is deliberately untouched and stays strict E.164 —
 * we must never dispatch an SMS to something that is not a dialable number.
 */
export const smsSenderSchema = z.union([
    e164PhoneSchema,
    z
        .string()
        .trim()
        .min(1)
        .max(32)
        .regex(/^[A-Za-z0-9][A-Za-z0-9 &.@_-]*$/, "Sender must be a phone number or an alphanumeric sender ID"),
]);
export const emailSchema = z.string().trim().toLowerCase().email();
export const isoDateSchema = z.coerce.date();
export const cursorSchema = z
    .string()
    .regex(/^[A-Za-z0-9_-]+$/, "Invalid cursor")
    .optional();
export const paginationQuerySchema = z.object({
    cursor: cursorSchema,
    limit: z.coerce.number().int().min(1).max(100).default(25),
    /**
     * Jump straight to the FINAL page.
     *
     * Keyset pagination cannot express "last page": there is no offset to skip to, and a total
     * would only say how many pages exist, not hand you a cursor for the last one. Walking forward
     * page by page would be O(rows / limit) requests.
     *
     * So the server flips the sort instead: the last page in descending order IS the first page in
     * ascending order. One indexed query, same cost as any other page, and the rows are reversed
     * before returning so the client still receives them newest-first.
     */
    last: z.coerce.boolean().optional(),
});
export const roleEnum = z.enum(["OWNER", "ADMIN", "MEMBER", "VIEWER"]);
export const userRoleEnum = z.enum(["USER", "ADMIN"]);
//# sourceMappingURL=common.js.map